import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const LOGO_URL = '/brand/twp-logo.png';
const BRAND_BROWN = '#8B4513';

// Cloud Function endpoint — sends branded reset emails via Office365 SMTP
// from nk@thewindowplacellc.com (not the firebaseapp.com sender that hits spam).
const PASSWORD_RESET_ENDPOINT =
  'https://us-central1-twp-calendar-61a9c.cloudfunctions.net/sendPasswordResetEmail';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // ✅ SAFE: Only restore email, never password
    const savedEmail = localStorage.getItem('twp_remembered_email');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();

    try {
      setError('');
      setResetMessage('');
      setLoading(true);

      // ✅ SAFE: Only persist email — never the password
      if (rememberMe) {
        localStorage.setItem('twp_remembered_email', email);
      } else {
        localStorage.removeItem('twp_remembered_email');
      }
      // ✅ Always ensure no legacy password entry lingers
      localStorage.removeItem('twp_remembered_password');

      const result = await signIn(email, password);

      if (result && result.redirectPath) {
        navigate(result.redirectPath);
      } else {
        navigate('/calendar');
      }

    } catch (err) {
      setError('Failed to sign in. Please check your credentials.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotPassword() {
    setError('');
    setResetMessage('');

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError('Enter your email above first, then click "Forgot password?"');
      return;
    }

    try {
      setResetLoading(true);

      // Call our Cloud Function — sends branded email via Office365 SMTP.
      // The function generates a Firebase reset link pointed at /auth/action
      // on the same origin the user is on, so password managers save the new
      // password under the TWP domain.
      const response = await fetch(PASSWORD_RESET_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: trimmedEmail,
          origin: window.location.origin,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to send reset email.');
      }

      setResetMessage(
        `Password reset link sent to ${trimmedEmail}. Check your inbox (and spam folder), then follow the link to set a new password.`
      );
    } catch (err) {
      console.error('Password reset error:', err);
      setError(err.message || 'Could not send reset email. Try again or contact Noah.');
    } finally {
      setResetLoading(false);
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #8B4513 0%, #A0522D 100%)',
      padding: '20px'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '420px',
        padding: '40px',
        background: 'var(--bg-card)',
        backdropFilter: 'blur(20px)',
        borderRadius: '24px',
        boxShadow: '0 20px 60px var(--bg-card-shadow)',
        border: '1px solid var(--glass-border)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <img
            src={LOGO_URL}
            alt="The Window Place LLC"
            style={{
              maxWidth: '180px',
              marginBottom: '16px',
              filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))'
            }}
          />
          <h1 style={{
            color: BRAND_BROWN,
            margin: '0 0 8px 0',
            fontSize: '28px',
            fontWeight: '700',
            background: 'linear-gradient(135deg, #8B4513 0%, #A0522D 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            TWP Hub
          </h1>
          <p style={{
            margin: 0,
            color: 'var(--text-secondary)',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            Internal Operations Portal
          </p>
        </div>

        {error && (
          <div style={{
            padding: '12px 16px',
            marginBottom: '20px',
            background: 'var(--error-bg)',
            border: '1px solid var(--error-border)',
            borderRadius: '12px',
            color: 'var(--error-text)',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            {error}
          </div>
        )}

        {resetMessage && (
          <div style={{
            padding: '12px 16px',
            marginBottom: '20px',
            background: 'var(--success-bg)',
            border: '1px solid var(--success-border)',
            borderRadius: '12px',
            color: 'var(--success-text)',
            fontSize: '14px',
            fontWeight: '500',
            lineHeight: '1.5'
          }}>
            {resetMessage}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '600',
              color: 'var(--text-primary)'
            }}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              style={{
                width: '100%',
                padding: '14px 16px',
                border: '2px solid var(--border-default)',
                borderRadius: '12px',
                fontSize: '16px',
                fontFamily: 'inherit',
                transition: 'all 0.2s',
                outline: 'none',
                WebkitAppearance: 'none',
                boxSizing: 'border-box',
                background: 'var(--bg-tertiary)',
                color: 'var(--text-primary)'
              }}
              onFocus={(e) => e.target.style.borderColor = 'var(--brand-brown)'}
              onBlur={(e) => e.target.style.borderColor = 'var(--border-default)'}
            />
          </div>

          <div style={{ marginBottom: '8px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '600',
              color: 'var(--text-primary)'
            }}>
              Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                style={{
                  width: '100%',
                  padding: '14px 48px 14px 16px',
                  border: '2px solid var(--border-default)',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontFamily: 'inherit',
                  transition: 'all 0.2s',
                  outline: 'none',
                  WebkitAppearance: 'none',
                  boxSizing: 'border-box',
                  background: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)'
                }}
                onFocus={(e) => e.target.style.borderColor = 'var(--brand-brown)'}
                onBlur={(e) => e.target.style.borderColor = 'var(--border-default)'}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                  fontSize: '16px',
                  color: 'var(--text-secondary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          {/* Forgot password? — sits right below the password field, right-aligned */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginBottom: '20px'
          }}>
            <button
              type="button"
              onClick={handleForgotPassword}
              disabled={resetLoading}
              style={{
                background: 'none',
                border: 'none',
                color: BRAND_BROWN,
                fontSize: '13px',
                fontWeight: '600',
                cursor: resetLoading ? 'not-allowed' : 'pointer',
                padding: '4px 0',
                textDecoration: 'underline',
                opacity: resetLoading ? 0.6 : 1
              }}
            >
              {resetLoading ? 'Sending reset link…' : 'Forgot password?'}
            </button>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              color: 'var(--text-secondary)',
              userSelect: 'none'
            }}>
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                style={{
                  width: '18px',
                  height: '18px',
                  cursor: 'pointer',
                  accentColor: BRAND_BROWN
                }}
              />
              <span style={{ fontWeight: '500' }}>Remember my email</span>
            </label>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '16px',
              background: loading ? 'var(--bg-disabled)' : `linear-gradient(135deg, ${BRAND_BROWN} 0%, #A0522D 100%)`,
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '700',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s',
              boxShadow: loading ? 'none' : '0 4px 12px rgba(139, 69, 19, 0.3)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              WebkitAppearance: 'none'
            }}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div style={{
          marginTop: '24px',
          paddingTop: '24px',
          borderTop: '1px solid var(--border-default)',
          textAlign: 'center'
        }}>
          <p style={{
            fontSize: '12px',
            color: 'var(--text-secondary)',
            margin: '0 0 12px 0'
          }}>
            Need help? Contact Noah or any manager at TWP
          </p>
          <p style={{
            fontSize: '12px',
            color: 'var(--text-tertiary)',
            margin: 0
          }}>
            The Window Place LLC {new Date().getFullYear()}
          </p>
        </div>
      </div>
    </div>
  );
}
